import React from 'react';

/**
 * 🎀 EnterpriseRibbon — Standardized ribbon container with consistent spacing
 *
 * Replaces manual div + desktopRibbonDividerClass usage.
 * Enforces: flex, align-items:center, gap:8px, no-wrap, overflow-x:hidden.
 * Each group gets: pr-16px, border-right, internal gap:6px.
 */

interface EnterpriseRibbonProps {
  moduleId?: string;
  children: React.ReactNode;
  className?: string;
}

export function EnterpriseRibbon({ moduleId, children, className = '' }: EnterpriseRibbonProps) {
  return (
    <div
      data-ribbon="true"
      data-module={moduleId}
      className={`enterprise-ribbon-zone officebar-surface w-full shrink-0 select-none text-[var(--ribbon-text-primary)] ${className}`.trim()}
      style={{ zIndex: 30, minHeight: 'var(--shell-ribbon-height, 48px)', background: 'var(--officebar-bg)' }}
    >
      <div
        className="enterprise-ribbon-inner flex items-center"
        style={{
          height: 'var(--shell-ribbon-height, 48px)',
          paddingLeft: 'var(--shell-header-inset, var(--shell-content-inset, 10px))',
          paddingRight: 'var(--shell-content-inset, 10px)',
          gap: '6px',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ─── Group ─────────────────────────────────────────────── */

interface EnterpriseRibbonGroupProps {
  label?: string;
  children: React.ReactNode;
  /** 'right' pushes group to ml-auto */
  position?: 'left' | 'center' | 'right';
  className?: string;
}

function EnterpriseRibbonGroup({ label, children, position, className = '' }: EnterpriseRibbonGroupProps) {
  return (
    <div
      className={`enterprise-ribbon-group flex h-full shrink-0 items-center pr-2 ${className}`.trim()}
      style={{
        borderRight: '1px solid rgba(255,255,255,0.08)',
        gap: '6px',
      }}
    >
      {children}
    </div>
  );
}

/* ─── Filters slot (rightmost, no border) ───────────────── */

interface EnterpriseRibbonFiltersProps {
  children: React.ReactNode;
  className?: string;
}

function EnterpriseRibbonFilters({ children, className = '' }: EnterpriseRibbonFiltersProps) {
  return (
    <div
      className={`enterprise-ribbon-filters ml-auto flex h-full items-center justify-end pr-1 ${className}`.trim()}
      style={{ gap: '8px' }}
    >
      {children}
    </div>
  );
}

/* ─── Attach sub-components ─────────────────────────────── */
EnterpriseRibbon.Group = EnterpriseRibbonGroup;
EnterpriseRibbon.Filters = EnterpriseRibbonFilters;

export default EnterpriseRibbon;
